import {
  getBillingHistory,
  getProfile,
  login,
  logout,
  LoginCredentials,
  getCurrentBill,
  getCurrentContract,
  getApartment,
  getCurrentTargets,
  getDebt,
  getSecurityDeposit,
  getContractTenants,
  getBuilding,
  getDocuments,
  getPublicNotices,
  getPublicEvents,
  getTerminationNotice,
  getKennoNotifications,
  getOwnForms,
  submitImpersonatorAuthNonce,
} from '../apis_kenno';
import { Contract } from './kennoTenantContext';

export enum KENNO_TENANT_ACTIONS {
  LOGOUT = 10000,
  LOGIN,
  LOAD_BILLING_HISTORY,
  SET_BILLING_FILTER,
  SET_BILLING_RANGE_START,
  SET_BILLING_RANGE_END,
  LOAD_CURRENT_BILL,
  LOAD_CONTRACT,
  LOAD_APARTMENT,
  LOAD_BUILDING,
  LOAD_DOCUMENTS,
  LOAD_CURRENT_TARGETS,
  LOAD_DEBT,
  LOAD_SECURITY_DEPOSIT,
  LOAD_PUBLIC_NOTICES,
  LOAD_PUBLIC_EVENTS,
  LOAD_TERMINATION_NOTICE,
  LOAD_KENNO_NOTIFICATIONS,
  LOAD_OWN_FORMS,
}

export enum KENNO_TENANT_EVENTS {
  LOGGEDIN = 20000,
  LOGGEDOUT,
  LOAD_BILLING_HISTORY_SUCCESS,
  LOAD_BILLING_HISTORY_FAILURE,
  LOAD_CURRENT_BILL_SUCCESS,
  LOAD_CURRENT_BILL_FAILURE,
  LOAD_CONTRACT_SUCCESS,
  LOAD_CONTRACT_FAILURE,
  LOAD_APARTMENT_SUCCESS,
  LOAD_APARTMENT_FAILURE,
  LOAD_BUILDING_SUCCESS,
  LOAD_BUILDING_FAILURE,
  LOAD_DOCUMENTS_SUCCESS,
  LOAD_DOCUMENTS_FAILURE,
  LOAD_CURRENT_TARGETS_SUCCESS,
  LOAD_CURRENT_TARGETS_FAILURE,
  LOAD_DEBT_SUCCESS,
  LOAD_DEBT_FAILURE,
  LOAD_SECURITY_DEPOSIT_SUCCESS,
  LOAD_SECURITY_DEPOSIT_FAILURE,
  LOAD_PUBLIC_NOTICES_SUCCESS,
  LOAD_PUBLIC_NOTICES_FAILURE,
  LOAD_PUBLIC_EVENTS_SUCCESS,
  LOAD_PUBLIC_EVENTS_FAILURE,
  LOAD_TERMINATION_NOTICE_SUCCESS,
  LOAD_TERMINATION_NOTICE_FAILURE,
  LOAD_KENNO_NOTIFICATIONS_SUCCESS,
  LOAD_KENNO_NOTIFICATIONS_FAILURE,
  LOAD_OWN_FORMS_SUCCESS,
  LOAD_OWN_FORMS_FAILURE,
}

export type Dispatcher = React.Dispatch<[KENNO_TENANT_EVENTS | KENNO_TENANT_ACTIONS, any?]>;

export async function loadUser(dispatch: Dispatcher) {
  try {
    dispatch([KENNO_TENANT_ACTIONS.LOGIN]);
    const profile = await getProfile();
    dispatch([KENNO_TENANT_EVENTS.LOGGEDIN, profile]);
  } catch (err) {
    dispatch([KENNO_TENANT_EVENTS.LOGGEDOUT]);
  }
}

export async function loginUser(dispatch: Dispatcher, credentials: LoginCredentials) {
  try {
    dispatch([KENNO_TENANT_ACTIONS.LOGIN]);
    await login(credentials);
    // Updating time of the last activity when signing in, so the user wouldn't be logged out immediately
    const now = Date.now();
    window.localStorage.setItem('lastActivity', now.toString());
    const profile = await getProfile();
    dispatch([KENNO_TENANT_EVENTS.LOGGEDIN, profile]);
  } catch (error) {
    dispatch([KENNO_TENANT_EVENTS.LOGGEDOUT]);
    throw error;
  }
}

export async function logoutUser(dispatch: Dispatcher) {
  try {
    dispatch([KENNO_TENANT_ACTIONS.LOGOUT]);
    await logout();
    dispatch([KENNO_TENANT_EVENTS.LOGGEDOUT]);
  } catch (err) {
    console.log('Logout failed:', err);
  }
}

export async function loadBillingHistory(dispatch: Dispatcher) {
  dispatch([KENNO_TENANT_ACTIONS.LOAD_BILLING_HISTORY]);
  try {
    const billingHistory = await getBillingHistory();
    dispatch([KENNO_TENANT_EVENTS.LOAD_BILLING_HISTORY_SUCCESS, billingHistory]);
  } catch (error) {
    dispatch([KENNO_TENANT_EVENTS.LOAD_BILLING_HISTORY_FAILURE]);
  }
}

export async function loadCurrentBill(dispatch: Dispatcher) {
  dispatch([KENNO_TENANT_ACTIONS.LOAD_CURRENT_BILL]);
  try {
    const currentBill = await getCurrentBill();
    dispatch([KENNO_TENANT_EVENTS.LOAD_CURRENT_BILL_SUCCESS, currentBill]);
  } catch (error) {
    dispatch([KENNO_TENANT_EVENTS.LOAD_CURRENT_BILL_FAILURE]);
  }
}

export async function loadCurrentTargets(dispatch: Dispatcher) {
  dispatch([KENNO_TENANT_ACTIONS.LOAD_CURRENT_TARGETS]);
  try {
    const targets = await getCurrentTargets();
    dispatch([KENNO_TENANT_EVENTS.LOAD_CURRENT_TARGETS_SUCCESS, targets]);
  } catch (error) {
    dispatch([KENNO_TENANT_EVENTS.LOAD_CURRENT_TARGETS_FAILURE]);
  }
}

export async function loadDebt(dispatch: Dispatcher) {
  dispatch([KENNO_TENANT_ACTIONS.LOAD_DEBT]);
  try {
    const debt = await getDebt();
    dispatch([KENNO_TENANT_EVENTS.LOAD_DEBT_SUCCESS, debt]);
  } catch (error) {
    dispatch([KENNO_TENANT_EVENTS.LOAD_DEBT_FAILURE]);
  }
}

export async function loadSecurityDeposit(dispatch: Dispatcher) {
  dispatch([KENNO_TENANT_ACTIONS.LOAD_SECURITY_DEPOSIT]);
  try {
    const securityDeposit = await getSecurityDeposit();
    dispatch([KENNO_TENANT_EVENTS.LOAD_SECURITY_DEPOSIT_SUCCESS, securityDeposit]);
  } catch (error) {
    dispatch([KENNO_TENANT_EVENTS.LOAD_SECURITY_DEPOSIT_FAILURE]);
  }
}

export async function loadContract(dispatch: Dispatcher) {
  dispatch([KENNO_TENANT_ACTIONS.LOAD_CONTRACT]);
  try {
    // Fetch both the contract and the contract tenants and merge them into one state
    const contract = (await getCurrentContract()) as Contract;
    contract.contractTenants = await getContractTenants();
    dispatch([KENNO_TENANT_EVENTS.LOAD_CONTRACT_SUCCESS, contract]);
  } catch (error) {
    dispatch([KENNO_TENANT_EVENTS.LOAD_CONTRACT_FAILURE]);
  }
}

export async function loadApartment(dispatch: Dispatcher) {
  dispatch([KENNO_TENANT_ACTIONS.LOAD_APARTMENT]);
  try {
    const apartment = await getApartment();
    dispatch([KENNO_TENANT_EVENTS.LOAD_APARTMENT_SUCCESS, apartment]);
  } catch (error) {
    dispatch([KENNO_TENANT_EVENTS.LOAD_APARTMENT_FAILURE]);
  }
}

export async function loadBuilding(dispatch: Dispatcher) {
  dispatch([KENNO_TENANT_ACTIONS.LOAD_BUILDING]);
  try {
    const building = await getBuilding();
    dispatch([KENNO_TENANT_EVENTS.LOAD_BUILDING_SUCCESS, building]);
  } catch (error) {
    dispatch([KENNO_TENANT_EVENTS.LOAD_BUILDING_FAILURE]);
  }
}

export async function loadDocuments(dispatch: Dispatcher) {
  dispatch([KENNO_TENANT_ACTIONS.LOAD_DOCUMENTS]);
  try {
    const documents = await getDocuments();
    dispatch([KENNO_TENANT_EVENTS.LOAD_DOCUMENTS_SUCCESS, documents]);
  } catch (error) {
    dispatch([KENNO_TENANT_EVENTS.LOAD_DOCUMENTS_FAILURE]);
  }
}

export async function loadPublicNotices(dispatch: Dispatcher) {
  dispatch([KENNO_TENANT_ACTIONS.LOAD_PUBLIC_NOTICES]);
  try {
    const notices = await getPublicNotices();
    dispatch([KENNO_TENANT_EVENTS.LOAD_PUBLIC_NOTICES_SUCCESS, notices]);
  } catch (error) {
    dispatch([KENNO_TENANT_EVENTS.LOAD_PUBLIC_NOTICES_FAILURE]);
  }
}

export async function loadPublicEvents(dispatch: Dispatcher) {
  dispatch([KENNO_TENANT_ACTIONS.LOAD_PUBLIC_EVENTS]);
  try {
    const events = await getPublicEvents();
    dispatch([KENNO_TENANT_EVENTS.LOAD_PUBLIC_EVENTS_SUCCESS, events]);
  } catch (error) {
    dispatch([KENNO_TENANT_EVENTS.LOAD_PUBLIC_EVENTS_FAILURE]);
  }
}

export async function loadTerminationNotice(dispatch: Dispatcher) {
  try {
    dispatch([KENNO_TENANT_ACTIONS.LOAD_TERMINATION_NOTICE]);
    const notice = await getTerminationNotice();
    dispatch([KENNO_TENANT_EVENTS.LOAD_TERMINATION_NOTICE_SUCCESS, notice]);
  } catch (err) {
    dispatch([KENNO_TENANT_EVENTS.LOAD_TERMINATION_NOTICE_FAILURE]);
  }
}

export async function loadKennoNotifications(dispatch: Dispatcher) {
  try {
    dispatch([KENNO_TENANT_ACTIONS.LOAD_KENNO_NOTIFICATIONS]);
    const notifications = await getKennoNotifications();
    dispatch([KENNO_TENANT_EVENTS.LOAD_KENNO_NOTIFICATIONS_SUCCESS, notifications]);
  } catch (err) {
    dispatch([KENNO_TENANT_EVENTS.LOAD_KENNO_NOTIFICATIONS_FAILURE]);
  }
}

export async function loadOwnForms(dispatch: Dispatcher) {
  try {
    dispatch([KENNO_TENANT_ACTIONS.LOAD_OWN_FORMS]);
    const ownForms = await getOwnForms();
    dispatch([KENNO_TENANT_EVENTS.LOAD_OWN_FORMS_SUCCESS, ownForms]);
  } catch (err) {
    dispatch([KENNO_TENANT_EVENTS.LOAD_OWN_FORMS_FAILURE]);
  }
}

export async function loginImpersonator(dispatch: Dispatcher, authNonce: string) {
  try {
    dispatch([KENNO_TENANT_ACTIONS.LOGIN]);
    await submitImpersonatorAuthNonce(authNonce);
    // Updating time of the last activity when signing in, so the user wouldn't be logged out immediately
    const now = Date.now();
    window.localStorage.setItem('lastActivity', now.toString());
    const profile = await getProfile();
    dispatch([KENNO_TENANT_EVENTS.LOGGEDIN, profile]);
  } catch (error) {
    dispatch([KENNO_TENANT_EVENTS.LOGGEDOUT]);
    throw error;
  }
}
